import React, { useState, useEffect } from 'react';
import FilterBadge from './FilterBadge';
import {  caregap_name } from '../../helpers';
import { Bookmark } from 'lucide-react';
import { fetchInsuranceGroup } from '../../services/insurance';

const FilterSummary = ({
  filter = {},
  setFilter = () => {},
  loadData = () => {},
  onClearFilters = () => {},
  setSelectedInsuranceOptions = () => {},
  clearFilterField = () => {},
  selectedMeasures = [],
  setSelectedMeasures = () => {},
  setPatientFormData = () => {},
  showSave = true,
  setShowSave = () => {},
  setSaveConfirmModal = () => {},
  setPreviousScrollPosition = () => {},
  setDataLoading = () => {},
}) => {
  const [insuranceOptions, setInsuranceOptions] = useState([]);
  const [userClearedFilters, setUserClearedFilters] = useState(false);

  useEffect(() => {
    const fetchInsurances = async () => {
      try {
        const response = await fetchInsuranceGroup();
        if (response) {
          setInsuranceOptions(response?.data);
        } else {
          setInsuranceOptions([]);
        }
      } catch (error) {
        console.error("Failed to load insurance options:", error);
      }
    };

    fetchInsurances();
  }, []);

  const getInsuranceNameById = (id) => {
    if (insuranceOptions.length === 0) return id;
    const insurance = insuranceOptions.find((option) => option.id === id);
    return insurance ? insurance.name : id;
  };

  const getQualityMeasureNameById = (id) => {
    const name = caregap_name(id);
    const status = selectedMeasures.find((measure) => measure.id === id)?.status;
    return `${name}, <b>Status:</b> ${status === 'completed' ? 'Done' : status === 'pending' ? 'Pending' : status}`;
    // return qualityMeasure ? qualityMeasure.name : id;
  };

  const removeFilter = (label, value) => {
    setShowSave(true);
    setDataLoading(true);
    let isFilterEmpty = false;
  
    setFilter((prevFilter) => {
      const newFilter = { ...prevFilter };
      switch (label) {
        case 'Start Age':
          newFilter.start_age = '';
          clearFilterField('start_age');
          break;
        case 'End Age':
          newFilter.end_age = '';
          clearFilterField('end_age');
          break;
        case 'Sex':
          newFilter.sex = '';
          clearFilterField('sex');
          break;
        case 'Last Visit':
          newFilter.last_visit = '';
          clearFilterField('last_visit');
          break;
        case 'Visited Last Days':
          newFilter.visited_last_days = '';
          clearFilterField('visited_last_days');
          break;
        case 'Not Visited Last Days':
          newFilter.not_visited_last_days = '';
          clearFilterField('not_visited_last_days');
          break;
        case 'Quality Measure':
          newFilter.quality_measure = newFilter.quality_measure.filter((qm) => qm.id !== value);
          break;
        case 'Insurance':
          newFilter.insurance_ids = newFilter.insurance_ids?.filter((id) => id !== value) || [];
          break;
        case 'Visit Type':
          newFilter.vt_name = '';
          clearFilterField('vt_name');
          break;
        case 'Gender':
          newFilter.gender = '';
          clearFilterField('gender');
          break;
        case 'Start Date':
          newFilter.start_date = '';
          clearFilterField('start_date');
          break;
        case 'End Date':
          newFilter.end_date = '';
          clearFilterField('end_date');
          break;
        case 'Practice Location':
          newFilter.practice_location = '';
          clearFilterField('practice_location');
          break;
        case 'Visit Status':
          newFilter.visit_status = '';
          clearFilterField('visit_status');
          break;
        default:
          break;
      }

      isFilterEmpty = [
        newFilter.start_age,
        newFilter.end_age,
        newFilter.sex,
        newFilter.last_visit,
        newFilter.visited_last_days,
        newFilter.not_visited_last_days,
        newFilter.insurance_ids,
        newFilter.quality_measure,
        newFilter.vt_name,
      ].every((val) => val === '' || (Array.isArray(val) && val.length === 0));
  
      loadData(newFilter);
      return newFilter;
    });

    setSelectedInsuranceOptions((prev) => prev?.filter((id) => id !== value));
    setSelectedMeasures((prev) => prev?.filter((measure) => measure.id !== value));
    setPatientFormData((prevData) => {
      const newData = { ...prevData };
      if (label === 'Insurance') {
        newData.insurance_ids = newData?.insurance_ids?.filter((id) => id !== value);
      } else if (label === 'Quality Measure') {
        newData.quality_measure = newData?.quality_measure?.filter((qm) => qm.id !== value);
      }
      return newData;
    });

    if (isFilterEmpty) {
      setUserClearedFilters(true);
    }
  };

  useEffect(() => {
    if (userClearedFilters) {
      onClearFilters();
      setUserClearedFilters(false);
    }
  }, [userClearedFilters, onClearFilters]);

  const filterItems = [
    { label: 'Sex', value: filter.sex },
    { label: 'Start Age', value: filter.start_age },
    { label: 'End Age', value: filter.end_age },
    { label: 'Insurance', value: filter.insurance_ids || [] },
    { label: 'Last Visit', value: filter.last_visit },
    { label: 'Visited Last Days', value: filter.visited_last_days },
    { label: 'Not Visited Last Days', value: filter.not_visited_last_days },
    { label: 'Quality Measure', value: filter.quality_measure || [] },
    { label: 'Visit Type', value: filter.vt_name },
    { label: 'Gender', value: filter.gender },
    { label: 'Start Date', value: filter.start_date },
    { label: 'End Date', value: filter.end_date },
    { label: 'Practice Location', value: filter.practice_location },
    { label: 'Visit Status', value: filter.visit_status }
  ];

  return (
    <div className="filters">
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {filterItems.map((item, index) => {
          if (Array.isArray(item.value)) {
            return item.value.length > 0 ? (
              item.value.map((val, idx) => (
                <FilterBadge
                  key={`${item.label}-${val.id || val}`}
                  label={item.label}
                  value={
                    item.label === 'Insurance'
                    ? getInsuranceNameById(val)
                    : item.label === 'Quality Measure'
                    ? getQualityMeasureNameById(val.id || val)
                    : val
                  } // Display name if available, otherwise id
                  onRemove={() => removeFilter(item.label, val.id || val)}
                />
              ))
            ) : null;
          }
            return item.value ? (
            <FilterBadge
              key={`${item.label}-${index}`}
              label={item.label === 'Sex' ? 'Gender' : item.label}
              value={
              item.label === 'Sex'
              ? item.value === 'F'
                ? 'Female'
                : item.value === 'M'
                ? 'Male'
                : item.value === 'A'
                ? 'All'
                : item.value === 'U'
                ? 'Other'
                : item.value
              : item.value
              }
              onRemove={() => removeFilter(item.label)}
            />
            ) : null;
        })}
      </div>
      {filterItems.some((item) => (Array.isArray(item.value) ? item.value.length > 0 : item.value)) && (
        showSave && (
          <button
            className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn filter_save_button"
            onClick={() => {
              setSaveConfirmModal(true);
              setPreviousScrollPosition(window.scrollY);
              window.scrollTo(0, 0);
            }}
          >
            <span>&nbsp;&nbsp;&nbsp;&nbsp;<Bookmark width={18} height={18} />&nbsp;Save Filter&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
          </button>
        )
      )}
    </div>
  );
};

export default FilterSummary;